<script setup lang="ts">
import CheckBox from '#components/elements/checkBox/CheckBox.vue';
import { useLanguage } from '#composables/useLanguage';
import { C4InputRadio, TagBase } from '@c4-links/vue3-links-components';
import { type ExportOptionsType } from '@carrefour-gcs/shared';
import { computed, toRef, watch } from 'vue';

const props = defineProps<{
  isTopFlopSalesAmountAvailable: boolean;
  isTopFlopSalesQuantityAvailable: boolean;
}>();

const exportOptions = defineModel<ExportOptionsType>({ required: true });

const isTopFlopSalesAmountAvailableRef = toRef(props.isTopFlopSalesAmountAvailable);
const isTopFlopSalesQuantityAvailableRef = toRef(props.isTopFlopSalesQuantityAvailable);

const { t } = useLanguage();

const radioInputClx = computed(() => (isChecked: boolean) => ({
  'border-border-main-primary': !isChecked,
  'border-interactive-active-main-primary': isChecked
}));

watch([() => exportOptions.value.withTopFlopSalesAmount, () => exportOptions.value.withTopFlopSalesQuantity], () => {
  if (exportOptions.value.withTopFlopSalesAmount || exportOptions.value.withTopFlopSalesQuantity) {
    exportOptions.value.exportType = 'excelWithTopFlop';
  }
});

watch(
  () => exportOptions.value.exportType,
  () => {
    if (exportOptions.value.exportType === 'csv') {
      isTopFlopSalesAmountAvailableRef.value = false;
      isTopFlopSalesQuantityAvailableRef.value = false;
    } else {
      isTopFlopSalesAmountAvailableRef.value = props.isTopFlopSalesAmountAvailable;
      isTopFlopSalesQuantityAvailableRef.value = props.isTopFlopSalesQuantityAvailable;
    }
  }
);
</script>

<template>
  <div class="size-full mx-auto">
    <div class="flex flex-col gap-4 my-4">
      <C4InputRadio
        v-model="exportOptions.exportType"
        :checked="exportOptions.exportType === 'excelWithTopFlop'"
        :class="radioInputClx(exportOptions.exportType === 'excelWithTopFlop')"
        name="exportType"
        :label="t('excelExport.popin.topFlop.radioBtn.label')"
        value="excelWithTopFlop"
        class="font-bold text-primary"
      >
        <div class="flex flex-col items-start gap-3">
          <div class="flex items-center gap-4 pt-2 font-normal text-default">
            <div class="flex gap-4">
              <span> {{ t('excelExport.popin.topFlop.export.checkbox.label') }}: </span>
              <div class="flex gap-5">
                <CheckBox
                  v-if="isTopFlopSalesAmountAvailable"
                  id="withTopFlopSalesAmount"
                  :checked="exportOptions.withTopFlopSalesAmount"
                  :disabled="!isTopFlopSalesQuantityAvailableRef"
                  :full-width="false"
                  :label="t('excelExport.popin.topFlop.export.checkbox.ca.label')"
                  @change="exportOptions.withTopFlopSalesAmount = $event"
                />
                <CheckBox
                  v-if="isTopFlopSalesQuantityAvailable"
                  id="withTopFlopSalesQuantity"
                  :checked="exportOptions.withTopFlopSalesQuantity"
                  :disabled="!isTopFlopSalesAmountAvailableRef"
                  :full-width="false"
                  :label="t('excelExport.popin.topFlop.export.checkbox.uvc.label')"
                  @change="exportOptions.withTopFlopSalesQuantity = $event"
                />
              </div>
            </div>
          </div>
          <TagBase
            v-if="
              exportOptions.exportType === 'excelWithTopFlop' &&
              exportOptions.withTopFlopSalesAmount === false &&
              exportOptions.withTopFlopSalesQuantity === false
            "
            icon="info"
            :is-closable="false"
            size="S"
            variant="warning"
            :full-width="false"
            >{{ t('excelExport.popin.topFlop.export.checkbox.required.message') }}</TagBase
          >
        </div>
      </C4InputRadio>
      <C4InputRadio
        v-model="exportOptions.exportType"
        :checked="exportOptions.exportType === 'csv'"
        :class="radioInputClx(exportOptions.exportType === 'csv')"
        name="exportType"
        :label="t('excelExport.popin.exportCsv.radioBtn.label')"
        value="csv"
        class="font-bold text-primary"
      >
        <div class="font-normal text-default pt-2">
          <p>{{ t('excelExport.popin.csv.export.includeData.label') }}</p>
        </div>
      </C4InputRadio>
    </div>
  </div>
</template>
